import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Send, RefreshCw } from 'lucide-react';
import { uuidv7 } from 'uuidv7';
import { t } from '../../utils/i18n';
import { useDmPersona } from '../../context/DmPersonaContext';
import { getConversation, getMessages, postMessage, markRead } from '../../api/dm';
import { observationAvailability } from '../../hooks/useObservationActivity';
import useBoundedPolling from '../../hooks/useBoundedPolling';
import ObservationStatus from '../../ui/ObservationStatus';
import UserAvatar from '../../components/UserAvatar';

const PAGE_SIZE = 50;
const POLL_INTERVAL_MS = 3000;
const READ_DEBOUNCE_MS = 300;
const SCROLL_BOTTOM_THRESHOLD_PX = 64;

function formatTime(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default function DmChat() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { accessToken, userId } = useDmPersona();

  const [peer, setPeer] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState('');
  const [unseenNewCount, setUnseenNewCount] = useState(0);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const scrollerRef = useRef(null);
  const oldestSeqRef = useRef(null);
  const latestSeqRef = useRef(0);
  const lastSentReadSeqRef = useRef(0);
  const readDebounceRef = useRef(null);
  const [initialEpoch, setInitialEpoch] = useState(0);
  const [initialLoaded, setInitialLoaded] = useState(false);

  const updateBoundsFromList = useCallback((list) => {
    if (!list.length) return;
    const oldest = list[0]?.seq ?? null;
    const latest = list[list.length - 1]?.seq ?? 0;
    oldestSeqRef.current = oldest;
    latestSeqRef.current = Math.max(latestSeqRef.current, latest);
  }, []);

  const scrollToBottom = useCallback((behavior = 'auto') => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  const sendReadIfAdvanced = useCallback(
    (lastReadSeq) => {
      if (!accessToken || !lastReadSeq || observationAvailability()) return;
      if (lastReadSeq <= lastSentReadSeqRef.current) return;
      if (readDebounceRef.current) clearTimeout(readDebounceRef.current);
      readDebounceRef.current = setTimeout(async () => {
        if (observationAvailability()) return;
        try {
          await markRead(conversationId, lastReadSeq, accessToken);
          lastSentReadSeqRef.current = Math.max(lastSentReadSeqRef.current, lastReadSeq);
        } catch {
          // best-effort
        }
      }, READ_DEBOUNCE_MS);
    },
    [accessToken, conversationId]
  );

  useEffect(() => {
    let cancelled = false;
    setLoadingInitial(true);
    setInitialLoaded(false);
    setError(null);
    setMessages([]);
    setHasMoreOlder(true);
    setUnseenNewCount(0);
    setIsAtBottom(true);
    oldestSeqRef.current = null;
    latestSeqRef.current = 0;
    lastSentReadSeqRef.current = 0;

    if (!accessToken) { setLoadingInitial(false); return () => { cancelled = true; }; }
    (async () => {
      try {
        const [peerInfo, page] = await Promise.all([
          getConversation(conversationId, accessToken),
          getMessages(conversationId, { limit: PAGE_SIZE }, accessToken),
        ]);
        if (cancelled) return;
        setInitialLoaded(true);
        setPeer(peerInfo);
        const sorted = [...(page.messages || [])].sort((a, b) => a.seq - b.seq);
        setMessages(sorted);
        updateBoundsFromList(sorted);
        setHasMoreOlder(sorted.length === PAGE_SIZE);
        setLoadingInitial(false);
        requestAnimationFrame(() => {
          scrollToBottom('auto');
          if (latestSeqRef.current > 0) {
            sendReadIfAdvanced(latestSeqRef.current);
          }
        });
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || t('dm.chat.errors.loadFailed'));
          setLoadingInitial(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (readDebounceRef.current) clearTimeout(readDebounceRef.current);
    };
  }, [conversationId, accessToken, initialEpoch, scrollToBottom, sendReadIfAdvanced, updateBoundsFromList]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      const atBottom = distanceFromBottom <= SCROLL_BOTTOM_THRESHOLD_PX;
      setIsAtBottom(atBottom);
      if (atBottom) {
        setUnseenNewCount(0);
        if (latestSeqRef.current) sendReadIfAdvanced(latestSeqRef.current);
      }
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [sendReadIfAdvanced]);

  const loadOlder = useCallback(async () => {
    if (loadingOlder || !hasMoreOlder || oldestSeqRef.current == null) return;
    setLoadingOlder(true);
    const el = scrollerRef.current;
    const prevHeight = el ? el.scrollHeight : 0;
    const prevTop = el ? el.scrollTop : 0;
    try {
      const page = await getMessages(
        conversationId,
        { limit: PAGE_SIZE, beforeSeq: oldestSeqRef.current },
        accessToken
      );
      const olderSorted = [...(page.messages || [])].sort((a, b) => a.seq - b.seq);
      if (olderSorted.length === 0) {
        setHasMoreOlder(false);
      } else {
        setMessages((prev) => [...olderSorted, ...prev]);
        oldestSeqRef.current = olderSorted[0].seq;
        if (olderSorted.length < PAGE_SIZE) setHasMoreOlder(false);
        requestAnimationFrame(() => {
          if (!scrollerRef.current) return;
          const delta = scrollerRef.current.scrollHeight - prevHeight;
          scrollerRef.current.scrollTop = prevTop + delta;
        });
      }
    } catch (err) {
      setError(err?.message || t('dm.chat.errors.loadFailed'));
    } finally {
      setLoadingOlder(false);
    }
  }, [conversationId, accessToken, loadingOlder, hasMoreOlder]);

  const topSentinelRef = useRef(null);
  useEffect(() => {
    const node = topSentinelRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadOlder();
      },
      { root: scrollerRef.current, threshold: 0.1 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [loadOlder]);

  const polling = useBoundedPolling({
    enabled: Boolean(accessToken && initialLoaded && !loadingInitial),
    scope: `${conversationId}:${accessToken}`,
    interval: POLL_INTERVAL_MS,
    fetch: () => getMessages(conversationId, { limit: 100, afterSeq: latestSeqRef.current || undefined }, accessToken),
    onData: page => {
      const newer = [...(page.messages || [])].sort((a, b) => a.seq - b.seq);
      if (newer.length === 0) return;
      setMessages((prev) => {
        const incomingByCid = new Map();
        newer.forEach((m) => {
          if (m.clientMessageId) incomingByCid.set(m.clientMessageId, m);
        });
        const filtered = prev.filter(
          (m) =>
            !(m.status === 'SENDING' && m.clientMessageId && incomingByCid.has(m.clientMessageId))
        );
        const existingSeqs = new Set(filtered.map((m) => m.seq));
        const toAppend = newer.filter((m) => !existingSeqs.has(m.seq));
        return [...filtered, ...toAppend];
      });
      latestSeqRef.current = Math.max(latestSeqRef.current, newer[newer.length - 1].seq);
      const incomingFromPeer = newer.filter((m) => Number(m.senderId) !== userId);
      if (isAtBottom) {
        requestAnimationFrame(() => scrollToBottom('smooth'));
        if (latestSeqRef.current) sendReadIfAdvanced(latestSeqRef.current);
      } else if (incomingFromPeer.length > 0) {
        setUnseenNewCount((c) => c + incomingFromPeer.length);
      }
    },
    onError: err => setError(err?.message || t('dm.chat.errors.loadFailed')),
  });
  const refreshObservation = () => {
    setError(null);
    if (!initialLoaded) setInitialEpoch(value => value + 1);
    polling.resume();
  };

  const handleSend = useCallback(async () => {
    const text = draft.trim();
    if (!text || sending) return;
    const clientMessageId = uuidv7();
    const optimistic = {
      messageId: `opt-${clientMessageId}`,
      conversationId,
      seq: (latestSeqRef.current || 0) + 0.5,
      senderId: userId,
      messageType: 'TEXT',
      body: text,
      mediaUrl: '',
      clientMessageId,
      status: 'SENDING',
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    setDraft('');
    setSending(true);
    requestAnimationFrame(() => scrollToBottom('smooth'));
    try {
      const sent = await postMessage(
        conversationId,
        { body: text, clientMessageId, messageType: 'TEXT' },
        accessToken
      );
      setMessages((prev) =>
        prev.map((m) =>
          m.clientMessageId === clientMessageId && m.status === 'SENDING'
            ? { ...sent, status: sent?.status || 'SENT' }
            : m
        )
      );
      if (sent?.seq) {
        latestSeqRef.current = Math.max(latestSeqRef.current, sent.seq);
        sendReadIfAdvanced(sent.seq);
      }
    } catch (err) {
      setMessages((prev) =>
        prev.map((m) =>
          m.clientMessageId === clientMessageId
            ? { ...m, status: 'FAILED', errorMessage: err?.message || '' }
            : m
        )
      );
    } finally {
      setSending(false);
    }
  }, [accessToken, conversationId, draft, sending, scrollToBottom, sendReadIfAdvanced, userId]);

  const handleRetry = useCallback(
    async (msg) => {
      if (!msg || msg.status !== 'FAILED') return;
      setMessages((prev) =>
        prev.map((m) =>
          m.clientMessageId === msg.clientMessageId
            ? { ...m, status: 'SENDING', errorMessage: undefined }
            : m
        )
      );
      try {
        const sent = await postMessage(
          conversationId,
          { body: msg.body, clientMessageId: msg.clientMessageId, messageType: 'TEXT' },
          accessToken
        );
        setMessages((prev) =>
          prev.map((m) =>
            m.clientMessageId === msg.clientMessageId
              ? { ...sent, status: sent?.status || 'SENT' }
              : m
          )
        );
        if (sent?.seq) latestSeqRef.current = Math.max(latestSeqRef.current, sent.seq);
      } catch (err) {
        setMessages((prev) =>
          prev.map((m) =>
            m.clientMessageId === msg.clientMessageId
              ? { ...m, status: 'FAILED', errorMessage: err?.message || '' }
              : m
          )
        );
      }
    },
    [conversationId, accessToken]
  );

  const peerLabel = useMemo(() => {
    if (peer?.peerUserId != null) return t('dm.chat.peer', { userId: peer.peerUserId });
    return '...';
  }, [peer]);

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] sm:h-[calc(100vh-4.5rem)] bg-dark-bg">
      <div className="flex items-center gap-3 px-4 sm:px-6 py-3 border-b border-dark-border bg-black">
        <button
          type="button"
          onClick={() => navigate('/dm/conversations')}
          className="p-1.5 rounded-lg text-dark-text-secondary hover:text-primary hover:bg-dark-surface"
          aria-label={t('common.back')}
        >
          <ArrowLeft size={18} />
        </button>
        <UserAvatar user={peer?.peerAvatar} size={36} className="w-9 h-9" variant="small" />
        <div className="min-w-0 flex-grow">
          <p className="text-sm font-semibold text-white truncate">{peerLabel}</p>
          {peer?.pairKey && (
            <p className="text-[11px] text-dark-text-muted truncate">{peer.pairKey}</p>
          )}
        </div>
      </div>

      <ObservationStatus {...polling} onResume={refreshObservation} disabled={!accessToken || loadingInitial} />
      <div ref={scrollerRef} className="flex-grow overflow-y-auto px-3 sm:px-6 py-4">
        {loadingInitial ? (
          <div className="text-center py-10 text-dark-text-secondary text-sm">{t('common.loading')}</div>
        ) : (
          <>
            <div ref={topSentinelRef} className="h-4" />
            {!hasMoreOlder && (
              <p className="text-center text-[11px] text-dark-text-muted py-2">{t('dm.chat.noMore')}</p>
            )}
            {loadingOlder && hasMoreOlder && (
              <p className="text-center text-[11px] text-dark-text-muted py-2">{t('dm.chat.loadingOlder')}</p>
            )}
            <ul className="space-y-2">
              {messages.map((m) => {
                const isMine = Number(m.senderId) === userId;
                const isFailed = m.status === 'FAILED';
                const isSending = m.status === 'SENDING';
                return (
                  <li
                    key={m.messageId || `${m.clientMessageId}-${m.seq}`}
                    className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[85%] sm:max-w-[75%] flex ${isMine ? 'flex-col items-end' : 'items-start gap-2'}`}>
                      {!isMine && (
                        <UserAvatar user={peer?.peerAvatar} size={24} className="w-6 h-6 mt-1" variant="mini" />
                      )}
                      <div className={`flex flex-col ${isMine ? 'items-end' : 'items-start'}`}>
                        <div
                          className={`px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
                            isMine
                              ? `bg-primary text-black ${isFailed ? 'opacity-70 ring-2 ring-red-500/60' : ''} ${isSending ? 'opacity-70' : ''}`
                              : 'bg-dark-card border border-dark-border text-white'
                          }`}
                        >
                          {m.body}
                        </div>
                        <div className="mt-0.5 flex items-center gap-2 text-[10px] text-dark-text-muted">
                          <span>{formatTime(m.createdAt)}</span>
                          {m.seq && Number.isInteger(m.seq) && (
                            <span className="opacity-70">#{m.seq}</span>
                          )}
                          {isSending && <span>{t('dm.chat.messageStatus.sending')}</span>}
                          {isFailed && (
                            <button
                              type="button"
                              onClick={() => handleRetry(m)}
                              className="text-red-300 hover:text-red-200 inline-flex items-center gap-1"
                            >
                              <RefreshCw size={10} />
                              {t('common.retry')}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
        {error && !loadingInitial && (
          <p className="mt-4 text-center text-xs text-red-300 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg">
            {error}
          </p>
        )}
      </div>

      {unseenNewCount > 0 && !isAtBottom && (
        <button
          type="button"
          onClick={() => {
            scrollToBottom('smooth');
            setUnseenNewCount(0);
          }}
          className="self-center mb-2 px-3 py-1.5 rounded-full bg-primary text-black text-xs font-bold shadow-lg hover:bg-primary/90"
        >
          {t('dm.chat.newMessages', { count: unseenNewCount })}
        </button>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSend();
        }}
        className="flex items-end gap-2 px-3 sm:px-6 py-3 border-t border-dark-border bg-black"
      >
        <textarea
          rows={1}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t('dm.chat.inputPlaceholder')}
          className="flex-grow bg-dark-surface border border-dark-border rounded-xl px-3 py-2 text-sm text-white placeholder-dark-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none max-h-32"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          maxLength={4000}
        />
        <button
          type="submit"
          disabled={sending || !draft.trim()}
          className="inline-flex items-center gap-1 bg-primary text-black font-bold px-3 sm:px-4 py-2 rounded-xl disabled:opacity-50 hover:bg-primary/90"
          aria-label={t('dm.chat.send')}
        >
          <Send size={16} />
          <span className="hidden sm:inline">
            {sending ? t('dm.chat.sending') : t('dm.chat.send')}
          </span>
        </button>
      </form>
      <div className="px-4 sm:px-6 pb-2 text-right">
        <Link to="/dm/conversations" className="text-[11px] text-dark-text-muted hover:text-primary">
          {t('common.back')}
        </Link>
      </div>
    </div>
  );
}
